import { isMimeType } from '@lexical/utils';
import { enqueueSnackbar } from 'notistack';

import normalizeImage, { INormalizedImage } from './normalizeImage';
import extractRtfPictures, { matchRtfPictures } from './rtfPictures';

/*
  The pictures in a paste or a drop, whatever put them there.

  Shared by the compose editor's ImagePastePlugin, which puts them into the Body, and the SMS text
  box, which takes them as a text's picture attachments - so a screenshot, "Copy image", or a paste
  from Word or classic Outlook comes through the same way into either. Every picture leaves here
  through normalizeImage, sized to send.
*/

// What a browser can decode onto a canvas; each leaves normalizeImage as a PNG or a JPEG.
export const PICTURE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/bmp'];

// How wide a picture first appears in the Body - and so in the email. Resizable from there.
const DISPLAY_MAX_WIDTH = 500;

export interface IPastedPictures {
  // The pasted HTML with each picture filled in (a data: URI, sized) or taken out; null when the
  // paste was only files.
  dom: Document | null;
  // Every picture that came through, in the order it appeared.
  pictures: INormalizedImage[];
  // How many could not be - see reportUnpasted.
  unpasted: number;
}

export const isPictureFile = (file: File) => isMimeType(file, PICTURE_TYPES);

export function displaySize(
  image: { width: number; height: number },
  shownWidth?: number,
  shownHeight?: number
) {
  // The size the source showed it at, when it says (Word does), else the picture's own.
  const shown = !!shownWidth && !!shownHeight;
  let width = shown ? shownWidth! : image.width;
  let height = shown ? shownHeight! : image.height;

  if (width > DISPLAY_MAX_WIDTH) {
    height = (height * DISPLAY_MAX_WIDTH) / width;
    width = DISPLAY_MAX_WIDTH;
  }

  return { width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) };
}

/*
  A data: picture's width and height, read from its header - PNG, GIF or JPEG, the only kinds
  normalizeImage and the server let through - without decoding it. Loading an image to measure it
  waits on the browser, which holds image work back in a tab that is not showing; this does not.
  The server reads the same headers (InlineImages.DimensionsOf).
*/
export function dataUriSize(src: string): { width: number; height: number } | null {
  const comma = src.indexOf(',');
  if (!src.startsWith('data:') || comma < 0) return null;

  let bytes: Uint8Array;
  try {
    const binary = atob(src.slice(comma + 1));
    bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  } catch {
    return null;
  }

  const size = (width: number, height: number) => (width > 0 && height > 0 ? { width, height } : null);
  const be16 = (i: number) => (bytes[i] << 8) | bytes[i + 1];

  // PNG: IHDR is always the first chunk; its width and height are big-endian at 16 and 20.
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[12] === 0x49 && bytes[15] === 0x52) {
    return size((be16(16) << 16) | be16(18), (be16(20) << 16) | be16(22));
  }

  // GIF: the logical screen size, little-endian, straight after the signature.
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return size(bytes[6] | (bytes[7] << 8), bytes[8] | (bytes[9] << 8));
  }

  // JPEG: walk the segments to the frame header (SOF0-15, less DHT, JPG and DAC).
  if (bytes[0] === 0xff && bytes[1] === 0xd8) {
    let i = 2;
    while (i + 8 < bytes.length && bytes[i] === 0xff) {
      const marker = bytes[i + 1];
      if (marker === 0xff) {
        // A fill byte between segments.
        i += 1;
      } else if (
        marker >= 0xc0 &&
        marker <= 0xcf &&
        marker !== 0xc4 &&
        marker !== 0xc8 &&
        marker !== 0xcc
      ) {
        return size(be16(i + 7), be16(i + 5));
      } else {
        i += 2 + be16(i + 2);
      }
    }
  }

  return null;
}

const pixels = (value: string | null) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

export function reportUnpasted(count: number) {
  if (!count) return;

  enqueueSnackbar(
    count === 1
      ? "1 image couldn't be pasted - copy and paste it on its own"
      : `${count} images couldn't be pasted - copy and paste them one at a time`,
    { variant: 'warning' }
  );
}

/*
  A paste or drop left out because another message was opened while its pictures were being sized
  (see usePasteTarget) - said, so it is not simply missing from what is sent. 'paste' is a whole
  paste of words and pictures, which goes with them.
*/
export function reportNotAdded(what: number | 'paste', again: 'Paste' | 'Drop' = 'Paste') {
  if (!what) return;

  let subject = 'What you pasted was';
  if (what === 1) subject = 'The picture was';
  else if (what !== 'paste') subject = `The ${what} pictures were`;

  const it = what === 'paste' || what === 1 ? 'it' : 'them';

  enqueueSnackbar(`${subject} not added because another message was opened. ${again} ${it} again.`, {
    variant: 'warning',
  });
}

function dataUrlToBlob(src: string): Blob | null {
  const match = /^data:(image\/[a-z0-9.+-]+);base64,([\s\S]*)$/i.exec(src);
  if (!match) return null;

  try {
    const binary = atob(match[2].replace(/\s/g, ''));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: match[1] });
  } catch {
    return null;
  }
}

const isFileSource = (src: string) => /^file:/i.test(src.trim());

export async function picturesFromFiles(
  files: File[]
): Promise<{ pictures: INormalizedImage[]; unpasted: number }> {
  const normalized = await Promise.all(files.map((file) => normalizeImage(file).catch(() => null)));

  return {
    pictures: normalized.filter((image): image is INormalizedImage => !!image),
    unpasted: normalized.filter((image) => !image).length,
  };
}

/*
  Give every <img> in a pasted document a picture the editor can hold, or take it out.

  Where each one's picture comes from:
    * a data: URI carries its own;
    * a file: path (Word, classic Outlook) is matched to the RTF's pictures by the size both give
      it - see matchRtfPictures - so a shape, which has an <img> but no picture, cannot shift the
      rest onto the wrong one;
    * failing both, a paste holding exactly one <img> and one image file is "Copy image" (or a lone
      Word picture): the file IS that picture, and beats a link back to the page it came from.

  Anything else - a link to a web page's picture, an Outlook on the web attachment, a shape Office
  only had as EMF - is removed rather than left to arrive as a broken or remote image.
*/
async function resolveDocumentPictures(
  images: HTMLImageElement[],
  rtf: string,
  files: File[]
): Promise<{ pictures: INormalizedImage[]; unpasted: number }> {
  const fromOffice = images.filter((img) => isFileSource(img.getAttribute('src') ?? ''));
  const matched =
    fromOffice.length && rtf
      ? matchRtfPictures(
          fromOffice.map((img) => ({
            width: pixels(img.getAttribute('width')),
            height: pixels(img.getAttribute('height')),
          })),
          extractRtfPictures(rtf)
        )
      : [];

  const results = await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute('src') ?? '';

      let blob: Blob | null = null;

      if (src.startsWith('data:')) {
        blob = dataUrlToBlob(src);
      } else if (isFileSource(src)) {
        const picture = matched[fromOffice.indexOf(img)];
        if (picture?.bytes) blob = new Blob([picture.bytes], { type: picture.contentType! });
      }

      if (!blob && images.length === 1 && files.length === 1) blob = files[0];

      const image = blob ? await normalizeImage(blob).catch(() => null) : null;

      if (!image) {
        img.remove();
        return null;
      }

      const size = displaySize(
        image,
        pixels(img.getAttribute('width')),
        pixels(img.getAttribute('height'))
      );

      img.setAttribute('src', image.src);
      img.setAttribute('width', String(size.width));
      img.setAttribute('height', String(size.height));

      return image;
    })
  );

  return {
    pictures: results.filter((image): image is INormalizedImage => !!image),
    unpasted: results.filter((image) => !image).length,
  };
}

// Whether a paste has words of its own, beside any pictures.
export const hasPastedText = (clipboard: DataTransfer) =>
  clipboard.types.includes('text/plain') && !!clipboard.getData('text/plain').trim();

/*
  What a paste carries, read now - a clipboard can only be read while its event is being handled -
  and handed back as the work that turns it into pictures. Null when it carries none: plain words,
  or cells from Excel, which puts a picture of the cells beside their text - the text is the paste.
*/
export function capturePastedPictures(
  clipboard: DataTransfer | null
): (() => Promise<IPastedPictures>) | null {
  if (!clipboard) return null;

  const html = clipboard.types.includes('text/html') ? clipboard.getData('text/html') : '';
  const dom = html ? new DOMParser().parseFromString(html, 'text/html') : null;
  const images = dom ? Array.from(dom.querySelectorAll('img')) : [];
  const files = Array.from(clipboard.files).filter(isPictureFile);

  if (images.length) {
    const rtf = clipboard.types.includes('text/rtf') ? clipboard.getData('text/rtf') : '';
    return async () => ({ dom, ...(await resolveDocumentPictures(images, rtf, files)) });
  }

  if (files.length && !hasPastedText(clipboard)) {
    return async () => ({ dom: null, ...(await picturesFromFiles(files)) });
  }

  return null;
}
